/**
 * Image Cache
 *
 * IndexedDB-based cache for captured still images. Simpler than recordingCache since images are captured locally, not downloaded.
 */

const logger = console.scope('ImageCache')

// IndexedDB configuration
const DB_NAME = 'oylo-image-cache'
const DB_VERSION = 1
const STORE_IMAGES = 'images'

// Database instance
let dbInstance = null
let dbInitPromise = null

// Cache state listeners
const cacheStateListeners = new Set()

// In-memory cache state: imageId -> { status, timestamp }
const cacheState = new Map()

// Track blob URLs created by getAllCachedImages for proper cleanup
let lastCachedImageUrls = []

/**
 * Initialize the image cache database
 */
async function openDatabase() {
	if (dbInstance) return dbInstance
	if (dbInitPromise) return dbInitPromise

	dbInitPromise = new Promise((resolve, reject) => {
		const request = indexedDB.open(DB_NAME, DB_VERSION)

		request.onerror = () => {
			logger.error('Failed to open image cache database:', request.error)
			// Reject first, then clear promise reference to ensure all awaiting callers get the error
			const error = request.error
			reject(error)
			dbInitPromise = null
		}

		request.onsuccess = () => {
			dbInstance = request.result
			dbInstance.onclose = () => {
				dbInstance = null
				dbInitPromise = null
			}
			resolve(dbInstance)
		}

		request.onupgradeneeded = event => {
			const db = event.target.result

			if (!db.objectStoreNames.contains(STORE_IMAGES)) {
				const store = db.createObjectStore(STORE_IMAGES, { keyPath: 'id' })
				store.createIndex('timestamp', 'timestamp', { unique: false })
				store.createIndex('size', 'size', { unique: false })
			}
		}
	})

	return dbInitPromise
}

/**
 * Notify cache state listeners
 */
function notifyCacheStateChange() {
	cacheStateListeners.forEach(cb => cb(new Map(cacheState)))
}

/**
 * Subscribe to cache state changes
 * @param {function} callback
 * @returns {function} Unsubscribe function
 */
export function subscribeToImageCacheState(callback) {
	cacheStateListeners.add(callback)
	return () => cacheStateListeners.delete(callback)
}

/**
 * Get all cache states
 * @returns {Map<string, { status: string, timestamp: number }>}
 */
export function getAllImageCacheStates() {
	return new Map(cacheState)
}

/**
 * Store an image in cache
 * @param {string} imageId
 * @param {Blob} blob
 * @param {string} filename
 */
export async function storeImage(imageId, blob, filename, thumbnailBlob = null) {
	try {
		const db = await openDatabase()
		return new Promise((resolve, reject) => {
			const tx = db.transaction(STORE_IMAGES, 'readwrite')
			const store = tx.objectStore(STORE_IMAGES)

			const record = {
				id: imageId,
				blob,
				filename,
				size: blob.size,
				timestamp: Date.now(),
				thumbnail: thumbnailBlob,
			}

			const request = store.put(record)
			request.onsuccess = () => {
				cacheState.set(imageId, { status: 'cached', timestamp: record.timestamp })
				notifyCacheStateChange()
				logger.info(`Stored image: ${imageId} (${formatBytes(blob.size)})`)
				resolve()
			}
			request.onerror = () => reject(request.error)
		})
	} catch (err) {
		logger.error('storeImage error:', err)
		throw err
	}
}

/**
 * Get a cached image
 * @param {string} imageId
 * @returns {Promise<{ blob: Blob, filename: string, timestamp: number } | null>}
 */
export async function getCachedImage(imageId) {
	try {
		const db = await openDatabase()
		return new Promise((resolve, reject) => {
			const tx = db.transaction(STORE_IMAGES, 'readonly')
			const store = tx.objectStore(STORE_IMAGES)
			const request = store.get(imageId)

			request.onsuccess = () => {
				const record = request.result
				if (record) {
					resolve({ blob: record.blob, filename: record.filename, timestamp: record.timestamp })
				} else {
					resolve(null)
				}
			}
			request.onerror = () => reject(request.error)
		})
	} catch (err) {
		logger.error('getCachedImage error:', err)
		return null
	}
}

/**
 * Revoke all blob URLs created by the last getAllCachedImages call Call this before fetching new images or when component unmounts
 */
export function revokeAllCachedImageUrls() {
	for (const url of lastCachedImageUrls) {
		URL.revokeObjectURL(url)
	}
	lastCachedImageUrls = []
}

/**
 * Get all cached images Note: Call revokeAllCachedImageUrls() before calling this again or on component unmount to prevent blob URL memory leaks
 * @returns {Promise<Array<{ id: string, filename: string, size: number, timestamp: number }>>}
 */
export async function getAllCachedImages() {
	// Revoke URLs from previous call to prevent memory leaks
	revokeAllCachedImageUrls()

	try {
		const db = await openDatabase()
		return new Promise((resolve, reject) => {
			const tx = db.transaction(STORE_IMAGES, 'readonly')
			const store = tx.objectStore(STORE_IMAGES)
			const request = store.getAll()

			request.onsuccess = () => {
				const records = request.result.map(r => {
					const item = {
						id: r.id,
						filename: r.filename,
						size: r.size,
						timestamp: r.timestamp,
					}
					// Create blob URL for thumbnail if available
					if (r.thumbnail) {
						const url = URL.createObjectURL(r.thumbnail)
						item.thumbnailUrl = url
						lastCachedImageUrls.push(url) // Track for later cleanup
					}
					return item
				})
				// Sort by timestamp descending (newest first)
				records.sort((a, b) => b.timestamp - a.timestamp)
				resolve(records)
			}
			request.onerror = () => reject(request.error)
		})
	} catch (err) {
		logger.error('getAllCachedImages error:', err)
		return []
	}
}

/**
 * Remove an image from cache
 * @param {string} imageId
 */
export async function deleteImage(imageId) {
	try {
		const db = await openDatabase()
		return new Promise((resolve, reject) => {
			const tx = db.transaction(STORE_IMAGES, 'readwrite')
			const store = tx.objectStore(STORE_IMAGES)
			const request = store.delete(imageId)

			request.onsuccess = () => {
				cacheState.delete(imageId)
				notifyCacheStateChange()
				logger.info(`Deleted image: ${imageId}`)
				resolve()
			}
			request.onerror = () => reject(request.error)
		})
	} catch (err) {
		logger.error('deleteImage error:', err)
	}
}

/**
 * Update an image's filename
 * @param {string} imageId
 * @param {string} newFilename
 */
export async function updateImageFilename(imageId, newFilename) {
	try {
		const db = await openDatabase()
		return new Promise((resolve, reject) => {
			const tx = db.transaction(STORE_IMAGES, 'readwrite')
			const store = tx.objectStore(STORE_IMAGES)
			const getRequest = store.get(imageId)

			getRequest.onsuccess = () => {
				const record = getRequest.result
				if (!record) {
					reject(new Error('Image not found'))
					return
				}

				// Update filename
				record.filename = newFilename

				const putRequest = store.put(record)
				putRequest.onsuccess = () => {
					notifyCacheStateChange()
					logger.info(`Renamed image ${imageId} to: ${newFilename}`)
					resolve()
				}
				putRequest.onerror = () => reject(putRequest.error)
			}
			getRequest.onerror = () => reject(getRequest.error)
		})
	} catch (err) {
		logger.error('updateImageFilename error:', err)
		throw err
	}
}

/**
 * Get an image's blob URL for display
 * @param {string} imageId
 * @returns {Promise<{ blobUrl: string, filename: string, revoke: () => void } | null>}
 */
export async function getImageForDisplay(imageId) {
	const cached = await getCachedImage(imageId)
	if (!cached) {
		logger.warn('Image not cached:', imageId)
		return null
	}

	const { blob, filename } = cached
	const blobUrl = URL.createObjectURL(blob)

	return {
		blobUrl,
		filename,
		revoke: () => URL.revokeObjectURL(blobUrl),
	}
}

/**
 * Initialize cache states from IndexedDB
 */
export async function initImageCacheStates() {
	try {
		const db = await openDatabase()
		return new Promise((resolve, reject) => {
			const tx = db.transaction(STORE_IMAGES, 'readonly')
			const store = tx.objectStore(STORE_IMAGES)
			const request = store.getAll()

			request.onsuccess = () => {
				const records = request.result
				records.forEach(r => {
					cacheState.set(r.id, { status: 'cached', timestamp: r.timestamp })
				})
				notifyCacheStateChange()
				resolve()
			}
			request.onerror = () => reject(request.error)
		})
	} catch (err) {
		logger.error('initImageCacheStates error:', err)
	}
}

/**
 * Get cache statistics
 */
export async function getImageCacheStats() {
	try {
		const db = await openDatabase()
		return new Promise((resolve, reject) => {
			const tx = db.transaction(STORE_IMAGES, 'readonly')
			const store = tx.objectStore(STORE_IMAGES)
			const request = store.openCursor()
			let count = 0
			let totalSize = 0

			request.onsuccess = event => {
				const cursor = event.target.result
				if (cursor) {
					count++
					totalSize += cursor.value.size || 0
					cursor.continue()
				} else {
					resolve({ count, totalSize, formattedSize: formatBytes(totalSize) })
				}
			}
			request.onerror = () => reject(request.error)
		})
	} catch (err) {
		logger.error('getImageCacheStats error:', err)
		return { count: 0, totalSize: 0, formattedSize: '0 B' }
	}
}

/**
 * Format bytes to human readable
 */
function formatBytes(bytes) {
	if (bytes === 0) return '0 B'
	const k = 1024
	const sizes = ['B', 'KB', 'MB', 'GB']
	const i = Math.floor(Math.log(bytes) / Math.log(k))
	return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i]
}
